import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { Bell, ArrowRight } from 'lucide-react';

function playBing() {
  try {
    const AudioCtx = (window as any).AudioContext || (window as any).webkitAudioContext;
    if (!AudioCtx) return;
    const ctx = new AudioCtx();
    const o = ctx.createOscillator();
    const g = ctx.createGain();
    o.type = 'sine';
    o.frequency.value = 880; // A5
    o.connect(g);
    g.connect(ctx.destination);
    const now = ctx.currentTime;
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.2, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.25);
    o.start();
    o.stop(now + 0.3);
  } catch {
    // no-op if auto-play blocked
  }
}

export function useLeadInsertNotifications(options?: { tenantId?: string }) {
  useEffect(() => {
    const channel = supabase
      .channel('lead-inserts')
      .on(
        'postgres_changes',
        { event: 'INSERT', schema: 'public', table: 'leads' },
        async (payload: any) => {
          const newLead = payload?.new || {};
          if (options?.tenantId && newLead.tenant_id !== options.tenantId) return;

          // fetch contact name
          let contactName = 'Unknown';
          try {
            const { data: contacts } = await supabase
              .from('lead_contacts')
              .select('name')
              .eq('lead_id', newLead.id)
              .limit(1);
            contactName = contacts?.[0]?.name || 'Unknown';
          } catch {}

          const productType = Array.isArray(newLead?.recommended_products) && newLead.recommended_products[0]
            ? newLead.recommended_products[0]
            : 'Lead';

          // Build link based on context
          const pathname = window.location.pathname || '';
          const isSystemAdmin = pathname.startsWith('/system');
          const linkHref = isSystemAdmin
            ? `/system/leads?lead=${encodeURIComponent(newLead.id)}`
            : `#lead-${encodeURIComponent(newLead.id)}`;

          // Play sound
          playBing();

          // Show banner bottom-right
          toast.custom(() => (
            <div className="pointer-events-auto w-[340px] rounded-lg border bg-white shadow-xl p-4">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 text-green-600">
                  <Bell className="h-5 w-5" />
                </div>
                <div className="flex-1">
                  <div className="font-semibold text-gray-900">Lead Received</div>
                  <div className="mt-1 text-sm text-gray-700">
                    <div><span className="text-gray-500">Product:</span> <span className="font-medium">{productType}</span></div>
                    <div><span className="text-gray-500">Customer:</span> <span className="font-medium">{contactName}</span></div>
                  </div>
                  <div className="mt-3">
                    <a
                      href={linkHref}
                      className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700"
                    >
                      View lead <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>
                </div>
              </div>
            </div>
          ), { position: 'bottom-right', duration: 5000 });
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [options?.tenantId]);
}

